import React, { useEffect, useState } from "react";

import { fetchKycStatus, fetchMe, presignKycUpload, submitKyc } from "../services/authService";
import { useAuth } from "../store/AuthContext";

const emptyForm = {
  document_type: "NID",
  document_front_url: "",
  document_back_url: "",
  selfie_url: "",
  address_proof_url: "",
};

export default function KycPage() {
  const { user, refreshUser } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [uploadBusy, setUploadBusy] = useState({
    front: false,
    back: false,
    selfie: false,
    address: false,
  });

  const inferContentType = (file) => {
    const explicit = (file?.type || "").toLowerCase().trim();
    if (["image/jpeg", "image/png", "application/pdf"].includes(explicit)) return explicit;
    const name = (file?.name || "").toLowerCase();
    if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
    if (name.endsWith(".png")) return "image/png";
    if (name.endsWith(".pdf")) return "application/pdf";
    return "";
  };

  const uploadKycFile = async (targetField, kind, file) => {
    if (!file) return;
    const ct = inferContentType(file);
    if (!ct) {
      setError("KYC file must be JPG/JPEG, PNG, or PDF.");
      return;
    }
    setError("");
    setMessage("");
    setUploadBusy((prev) => ({ ...prev, [kind]: true }));
    try {
      const { data } = await presignKycUpload({
        file_name: file.name,
        content_type: ct,
        kind,
      });
      if (data.max_bytes && file.size > data.max_bytes) {
        setError(`File too large (max ${Math.round(data.max_bytes / 1024 / 1024)} MB).`);
        return;
      }
      const putRes = await fetch(data.upload_url, {
        method: "PUT",
        headers: { "Content-Type": data.content_type || ct },
        body: file,
      });
      if (!putRes.ok) {
        setError(`KYC upload failed (${putRes.status}). Check S3 CORS and bucket permissions.`);
        return;
      }
      setForm((prev) => ({ ...prev, [targetField]: data.key }));
      setMessage("Document uploaded and linked.");
    } catch (err) {
      const d = err.response?.data;
      setError(typeof d === "string" ? d : d?.detail || "KYC upload failed.");
    } finally {
      setUploadBusy((prev) => ({ ...prev, [kind]: false }));
    }
  };

  const load = async () => {
    const { data } = await fetchKycStatus();
    setSubmissions(Array.isArray(data) ? data : data?.results || []);
  };

  useEffect(() => {
    load().catch(() => setSubmissions([]));
  }, []);

  const latest = submissions[0];
  const isPendingReview = user?.kyc_status === "PENDING";
  const isApproved = user?.kyc_status === "APPROVED";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (Object.values(uploadBusy).some(Boolean)) {
      setError("Please wait for current file upload(s) to finish.");
      return;
    }
    if (!form.document_front_url.trim()) {
      setError("Please provide front document URL or upload a front document file.");
      return;
    }
    if (form.document_type === "NID" && !form.document_back_url.trim()) {
      setError("National ID requires back document URL or file upload.");
      return;
    }
    if (!form.selfie_url.trim()) {
      setError("Please provide selfie URL or upload a selfie file.");
      return;
    }
    if (!form.address_proof_url.trim()) {
      setError("Please provide address proof URL or upload a file.");
      return;
    }
    try {
      const payload = {
        document_type: form.document_type,
        document_front_url: form.document_front_url.trim(),
        document_back_url: form.document_back_url.trim(),
        selfie_url: form.selfie_url.trim(),
        address_proof_url: form.address_proof_url.trim(),
      };
      await submitKyc(payload);
      setMessage("KYC submitted. Our team will review your documents.");
      setForm(emptyForm);
      await load();
      const me = await fetchMe();
      refreshUser(me.data);
    } catch (err) {
      const d = err.response?.data;
      setError(typeof d === "string" ? d : d?.detail || JSON.stringify(d) || "Submission failed.");
    }
  };

  return (
    <div className="page">
      <h2>Identity verification (KYC)</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Upload your documents to a private location (for example your organization&apos;s secure file share), then paste the
        HTTPS links below. <strong>Approved KYC is required before buying shares</strong> and for wallet (BDT) withdrawals. You can deposit to your wallet while KYC is pending.
      </p>
      {message && <p className="msg">{message}</p>}
      {error && <p className="msg msg-error">{error}</p>}

      {isApproved && (
        <div className="card stack" style={{ padding: 14 }}>
          <p>
            <span className="badge badge-APPROVED">Approved</span> Your identity is verified.
          </p>
        </div>
      )}

      {isPendingReview && (
        <div className="card stack" style={{ padding: 14 }}>
          <p>
            <span className="badge badge-PENDING">Pending</span> Your latest submission is under review. You will be notified
            when the decision is recorded.
          </p>
          {latest?.submitted_at && (
            <p className="muted">
              Submitted: {new Date(latest.submitted_at).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {!isApproved && !isPendingReview && (
        <form className="card stack" style={{ padding: 14, maxWidth: 560 }} onSubmit={onSubmit}>
          {user?.kyc_status === "REJECTED" && latest?.rejection_reason && (
            <div className="card" style={{ padding: 12, background: "#fef2f2", borderColor: "#fecaca" }}>
              <strong>Previous submission rejected</strong>
              <p style={{ margin: "8px 0 0" }}>{latest.rejection_reason}</p>
            </div>
          )}
          <label>
            Document type
            <select
              value={form.document_type}
              onChange={(e) => setForm({ ...form, document_type: e.target.value })}
            >
              <option value="NID">National ID (NID)</option>
              <option value="PASSPORT">Passport</option>
            </select>
          </label>
          <label>
            ID document — front (URL)
            <input
              type="text"
              value={form.document_front_url}
              onChange={(e) => setForm({ ...form, document_front_url: e.target.value })}
              placeholder="https://... or documents/..."
            />
            <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                onChange={(e) => uploadKycFile("document_front_url", "front", e.target.files?.[0])}
              />
              {uploadBusy.front && <span className="muted" style={{ fontSize: 12 }}>Uploading...</span>}
            </div>
          </label>
          <label>
            ID document — back (URL){form.document_type === "NID" ? " — required for NID" : " — optional for passport"}
            <input
              type="text"
              value={form.document_back_url}
              onChange={(e) => setForm({ ...form, document_back_url: e.target.value })}
              placeholder="https://... or documents/..."
            />
            <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                onChange={(e) => uploadKycFile("document_back_url", "back", e.target.files?.[0])}
              />
              {uploadBusy.back && <span className="muted" style={{ fontSize: 12 }}>Uploading...</span>}
            </div>
          </label>
          <label>
            Selfie holding ID (URL)
            <input
              type="text"
              value={form.selfie_url}
              onChange={(e) => setForm({ ...form, selfie_url: e.target.value })}
              placeholder="https://... or documents/..."
            />
            <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                onChange={(e) => uploadKycFile("selfie_url", "selfie", e.target.files?.[0])}
              />
              {uploadBusy.selfie && <span className="muted" style={{ fontSize: 12 }}>Uploading...</span>}
            </div>
          </label>
          <label>
            Proof of address (URL)
            <input
              type="text"
              value={form.address_proof_url}
              onChange={(e) => setForm({ ...form, address_proof_url: e.target.value })}
              placeholder="https://... or documents/..."
            />
            <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                onChange={(e) => uploadKycFile("address_proof_url", "address", e.target.files?.[0])}
              />
              {uploadBusy.address && <span className="muted" style={{ fontSize: 12 }}>Uploading...</span>}
            </div>
          </label>
          <button type="submit">Submit for review</button>
        </form>
      )}
    </div>
  );
}
